/**
 * Browser-side security event monitoring (Phase A.5.1).
 *
 * Defense-in-depth alongside the CSP `report-uri` directive in vercel.json:
 *   • report-uri  — browser POSTs CSP reports directly to Sentry's CSP
 *                   endpoint. Works before page JS runs, survives JS errors.
 *   • this module — JS-side listeners as a backup; also catches
 *                   browser events that aren't covered by `report-uri`
 *                   (suspicious URL params, SRI failures).
 *
 * Each event lands in Sentry as a `captureMessage` with `level: 'warning'`
 * and a `kind:` tag for filtering: `kind:csp-violation`,
 * `kind:suspicious-url`, `kind:sri-failure`.
 *
 * Filter in Sentry Issues with the search bar: `kind:csp-violation`
 */

import * as Sentry from '@sentry/react';

const SUSPICIOUS_URL_PATTERNS: ReadonlyArray<RegExp> = [
  /<script[\s>]/i,
  /javascript:/i,
  /\bon\w+\s*=/i, // onerror=, onclick=, onload=, etc.
  /\bunion\s+select\b/i, // SQLi
  /\bdrop\s+(table|database)\b/i, // SQLi
  /<iframe[\s>]/i,
];

export function installSecurityMonitoring(): void {
  if (typeof window === 'undefined') return;

  // 1. CSP violations → Sentry (defense-in-depth alongside the
  //    `report-uri` directive; this fires from the browser's
  //    SecurityPolicyViolationEvent dispatched on document/window).
  //    Title is prefixed `🚨 OKOS Security` so email subject lines
  //    are unmistakable in the inbox.
  window.addEventListener('securitypolicyviolation', (e) => {
    Sentry.captureMessage(`🚨 OKOS Security: CSP violation - ${e.violatedDirective}`, {
      level: 'warning',
      tags: {
        kind: 'csp-violation',
        directive: e.violatedDirective,
        disposition: e.disposition,
      },
      extra: {
        blockedURI: e.blockedURI,
        documentURI: e.documentURI,
        sourceFile: e.sourceFile,
        lineNumber: e.lineNumber,
        columnNumber: e.columnNumber,
        sample: e.sample,
        effectiveDirective: e.effectiveDirective,
        originalPolicy: e.originalPolicy?.slice(0, 200), // truncate, full policy is huge
      },
    });
  });

  // 2. Suspicious URL patterns on initial load (XSS payloads,
  //    SQL keywords). Don't block — observe + alert.
  //
  // Browsers URL-encode special chars in the address bar (e.g.
  // `<` → `%3C`), so the realistic phishing-link case never matches
  // the raw form. Test against BOTH raw and decoded.
  const rawUrl = `${location.search}${location.hash}`;
  let decodedUrl = rawUrl;
  try {
    decodedUrl = decodeURIComponent(rawUrl);
  } catch {
    /* malformed %xx sequences — keep raw, regex will fall back */
  }
  for (const pattern of SUSPICIOUS_URL_PATTERNS) {
    if (pattern.test(rawUrl) || pattern.test(decodedUrl)) {
      Sentry.captureMessage('🚨 OKOS Security: Suspicious URL pattern detected', {
        level: 'warning',
        tags: {
          kind: 'suspicious-url',
          pattern: pattern.source.slice(0, 50),
        },
        extra: {
          search: location.search,
          hash: location.hash,
          decoded: decodedUrl !== rawUrl ? decodedUrl : undefined,
          referrer: document.referrer,
          userAgent: navigator.userAgent,
        },
      });
      break; // one report per page load is enough
    }
  }

  // 3. SRI (Subresource Integrity) failures. Only fires today if a
  //    `<script>` or `<link>` element has an `integrity` attribute and
  //    the resource hash doesn't match. We don't currently use SRI on
  //    any first- or third-party scripts, so this is preventive — when
  //    we add SRI hashes to vendor scripts later, this catches tamper
  //    attempts. Capture phase (third arg `true`) so resource errors
  //    bubble to us.
  window.addEventListener(
    'error',
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName?.toLowerCase();
      if (
        (tag === 'script' || tag === 'link') &&
        target.hasAttribute('integrity')
      ) {
        Sentry.captureMessage(`🚨 OKOS Security: SRI failure - ${target.tagName} blocked`, {
          level: 'warning',
          tags: { kind: 'sri-failure', tagName: tag },
          extra: {
            src:
              (target as HTMLScriptElement).src ??
              (target as HTMLLinkElement).href,
            integrity: target.getAttribute('integrity'),
          },
        });
      }
    },
    true,
  );
}
